import { useCallback, useEffect, useState } from "react";
import { RankingsApi } from "../../../api/rankingsApi";
import {
  ANDROID_COUNTRIES,
  AppSearchResult,
  Category,
  DeviceType,
  GAME_SUBCATEGORIES,
  IOS_COUNTRIES,
  RankingsAllResponse,
  StoreType,
} from "../../../model/rankings";

interface UseAppStoreRankingsLogicOptions {
  onStoreChange?: () => void;
}

export function useAppStoreRankingsLogic(
  options?: UseAppStoreRankingsLogicOptions
) {
  // Combined filter state - single object to ensure atomic updates
  const [filters, setFilters] = useState({
    store: "android" as StoreType,
    country: "us",
    category: "",
    device: "iphone" as DeviceType,
    subcategory: "",
  });

  // Data state
  const [rankings, setRankings] = useState<RankingsAllResponse | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<AppSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  // Get countries based on store
  const countries = filters.store === "ios" ? IOS_COUNTRIES : ANDROID_COUNTRIES;

  // Fetch categories when store changes
  useEffect(() => {
    RankingsApi.fetchCategories(filters.store)
      .then(setCategories)
      .catch((err) => console.error("Failed to fetch categories:", err));
  }, [filters.store]);

  // Fetch rankings when filters change
  const fetchRankings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await RankingsApi.fetchRankings(
        filters.store,
        filters.country,
        filters.subcategory || filters.category || undefined,
        200,
        filters.store === "ios" ? filters.device : undefined
      );
      setRankings(data);
    } catch (err) {
      setError("Failed to load rankings. Please try again.");
      console.error("Failed to fetch rankings:", err);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    // Clear search when filters change to avoid confusion
    setSearchQuery("");
    setSearchResults([]);
    fetchRankings();
  }, [fetchRankings]);

  // Handle store change - reset all filters to defaults for that store
  const handleStoreChange = (newStore: StoreType) => {
    // Call optional callback (e.g., clearPreloadCache in Default view)
    options?.onStoreChange?.();

    setFilters({
      store: newStore,
      country: "us",
      category: "",
      device: "iphone",
      subcategory: "",
    });
  };

  // Handle category change - reset subcategory
  const handleCategoryChange = (newCategory: string) => {
    setFilters((prev) => ({
      ...prev,
      category: newCategory,
      subcategory: "",
    }));
  };

  // Search handlers
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      return;
    }
    setIsSearchModalOpen(true);
    setIsSearching(true);
    try {
      const response = await RankingsApi.searchApps(
        filters.store,
        searchQuery,
        filters.country
      );
      setSearchResults(response.results);
    } catch (err) {
      console.error("Search failed:", err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setIsSearchModalOpen(false);
  };

  const handleCloseSearchModal = () => {
    setIsSearchModalOpen(false);
  };

  // Helper to detect if current category is Games
  const isGamesCategory = (cat: string) =>
    cat.toLowerCase().includes("game") || cat === "6014";

  // Sort Android categories: Overall, Application, Game first, then ABC
  const sortAndroidCategories = (cats: Category[]): Category[] => {
    // Priority by ID (what API returns) or name
    const getPriority = (cat: Category): number => {
      const id = cat.id.toUpperCase();
      const name = cat.name.toLowerCase();
      // Overall/Top Overall should be first
      if (id === "OVERALL" || name.includes("overall")) return 0;
      // Application/App Store Rankings second
      if (
        id === "APPLICATION" ||
        name === "application" ||
        name === "app store rankings"
      )
        return 1;
      // Game/Games third
      if (id === "GAME" || id === "GAMES" || name === "game" || name === "games")
        return 2;
      // Everything else
      return 999;
    };

    return [...cats].sort((a, b) => {
      const aPriority = getPriority(a);
      const bPriority = getPriority(b);

      // If different priorities, sort by priority
      if (aPriority !== bPriority) return aPriority - bPriority;
      // Same priority (both are non-priority) - sort alphabetically
      return a.name.localeCompare(b.name);
    });
  };

  // Generate options for dropdowns
  const countryOptions = countries.map((c) => ({
    value: c.code,
    label: c.name,
  }));

  // Sort categories for Android
  const sortedCategories =
    filters.store === "android"
      ? sortAndroidCategories(categories)
      : categories;

  const categoryOptions = [
    { value: "", label: "All Categories" },
    ...sortedCategories.map((c) => ({
      value: c.id,
      label: c.name,
    })),
  ];

  const deviceOptions = [
    { value: "iphone", label: "iPhone" },
    { value: "ipad", label: "iPad" },
  ];

  const subcategoryOptions = [
    { value: "", label: "All Games" },
    ...GAME_SUBCATEGORIES.map((s) => ({
      value: s.id,
      label: s.name,
    })),
  ];

  // Get search placeholder with store and country
  const selectedCountryName =
    countries.find((c) => c.code === filters.country)?.name ||
    "United States";
  const storeName = filters.store === "ios" ? "iOS" : "Android";
  const searchPlaceholder = `Search ${storeName} apps in ${selectedCountryName}...`;

  return {
    // State
    filters,
    setFilters,
    rankings,
    categories,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    isSearchModalOpen,

    // Derived values
    countries,
    countryOptions,
    categoryOptions,
    deviceOptions,
    subcategoryOptions,
    sortedCategories,
    searchPlaceholder,
    selectedCountryName,
    storeName,

    // Functions
    fetchRankings,
    handleStoreChange,
    handleCategoryChange,
    handleSearch,
    handleClearSearch,
    handleCloseSearchModal,
    isGamesCategory,
  };
}
